import React, { useState } from 'react'
import { FaClinicMedical, FaPencilAlt, FaUserMd, FaVials } from 'react-icons/fa'

interface ClinicInfoCardsProps {
  clinicName: string
  clinicCity: string
  clinicState: string
  doctorName: string
  doctorCrm: string
  treatmentType: string
  treatmentDate: string
  onEditDoctor?: (doctorName: string, doctorCrm: string) => Promise<void>
}

const ClinicInfoCards: React.FC<ClinicInfoCardsProps> = ({
  clinicName,
  clinicCity,
  clinicState,
  doctorName,
  doctorCrm,
  treatmentType,
  treatmentDate,
  onEditDoctor,
}) => {
  const [isEditingDoctor, setIsEditingDoctor] = useState(false)
  const [editDoctorName, setEditDoctorName] = useState('')
  const [editDoctorCrm, setEditDoctorCrm] = useState('')
  const [isSavingDoctor, setIsSavingDoctor] = useState(false)

  const handleStartEditDoctor = () => {
    setEditDoctorName(doctorName)
    setEditDoctorCrm(doctorCrm)
    setIsEditingDoctor(true)
  }

  const handleSaveDoctor = async () => {
    if (!onEditDoctor) return

    setIsSavingDoctor(true)

    try {
      await onEditDoctor(editDoctorName.trim(), editDoctorCrm.trim())
      setIsEditingDoctor(false)
    } catch (error) {
      // Feedback is handled by the caller (SweetAlert); keep editing open
    } finally {
      setIsSavingDoctor(false)
    }
  }

  return (
    <div className="row g-3 align-items-stretch">
      <div className="col-12 col-sm-12 col-lg-4">
        <div className="info-card">
          <div className="info-card__icon info-card__icon--clinic">
            <FaClinicMedical />
          </div>
          <div className="info-card__content">
            <span className="info-card__title">Clínica</span>
            <h6 className="info-card__name">{clinicName}</h6>
            <div className="info-card__details">
              <span className="info-card__label">Cidade</span>
              <p className="info-card__value">{clinicCity}</p>
            </div>
            <div className="info-card__details">
              <span className="info-card__label">UF</span>
              <p className="info-card__value">
                {clinicState}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="col-12 col-sm-12 col-lg-4">
        <div className="info-card info-card--doctor">
          <div className="info-card__icon info-card__icon--doctor">
            <FaUserMd />
          </div>
          <div className="info-card__content">
            <div className="d-flex align-items-center justify-content-between">
              <span className="info-card__title">Médico</span>
              {onEditDoctor && !isEditingDoctor && (
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 d-flex align-items-center"
                  style={{ fontSize: '12px', gap: '4px', textDecoration: 'none' }}
                  onClick={handleStartEditDoctor}
                >
                  <FaPencilAlt size={10} /> Alterar
                </button>
              )}
            </div>
            {isEditingDoctor ? (
              <div className="mt-1">
                <div className="form-group mb-2">
                  <label className="mb-0" style={{ fontSize: '12px' }}>Nome do Médico</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editDoctorName}
                    onChange={(e) => setEditDoctorName(e.target.value)}
                    disabled={isSavingDoctor}
                  />
                </div>
                <div className="form-group mb-2">
                  <label className="mb-0" style={{ fontSize: '12px' }}>CRM</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={editDoctorCrm}
                    onChange={(e) => setEditDoctorCrm(e.target.value)}
                    disabled={isSavingDoctor}
                  />
                </div>
                <div className="d-flex" style={{ gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleSaveDoctor}
                    disabled={isSavingDoctor}
                  >
                    {isSavingDoctor ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setIsEditingDoctor(false)}
                    disabled={isSavingDoctor}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <>
                {doctorName ? (
                  <h6 className="info-card__name">{doctorName}</h6>
                ) : (
                  <p className="info-card__value">Não informado</p>
                )}
                <div className="info-card__details">
                  <span className="info-card__label">CRM</span>
                  <p className="info-card__value">{doctorCrm || 'Não informado'}</p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="col-12 col-sm-12 col-lg-4">
        <div className="info-card">
          <div className="info-card__icon info-card__icon--treatment">
            <FaVials />
          </div>
          <div className="info-card__content">
            <span className="info-card__title">Tratamento</span>
            <h6 className="info-card__name">{treatmentType || '-'}</h6>
            <div className="info-card__details">
              <span className="info-card__label">Data Prevista</span>
              <p className="info-card__value">{treatmentDate}</p>
            </div>
            <div className="info-card__details">
              <span className="info-card__label">Quantidade</span>
              <p className="info-card__value">01 Amostra de Sêmen</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClinicInfoCards
